import React, { useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { getOrderDetails, syncOrderWithPos, updateOrderDeliveryDate } from "store/actions/orderAction"
import { useSelector } from "react-redux"
import { ShimmerSimpleGallery } from "react-shimmer-effects"
import TableShimmer from "components/Shared/TableShimmer"
import BackButton from "components/Shared/BackButton"
import StarRatings from "react-star-ratings"
import formatDateTime from "components/Shared/formatDateTime"
import LtrText from "components/Shared/LtrText"
import isPermissionEnabled, { getSubPermissions } from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import { showAlert } from "store/reducers/alertReducer"
import { DOMAIN_URL } from "helpers/api_helper"
import {
  buildOrderPosSource,
  getTenantIntegrationConnection,
  hasExternalPosIntegration,
  isFnbPosProvider,
  isOrderPosBranchLinked,
  isSalasaProvider,
  resolveOrderPosIntegrationCode,
  shouldShowOrderPosDetails,
} from "utils/orderPosUtils"

const POS_PUSH_FAILED_MESSAGE =
  "Unable to send the order to POS. Please try again."
import useTenantPosConnections from "hooks/useTenantPosConnections"
import OrderPosDetailsPanel from "components/Orders/OrderPosDetailsPanel"
import { useAssignedBrands } from "hooks/useAssignedBrands"
import {
  findAssignedBrandForOrder,
  resolveOrderDeliveryDate,
  resolveOrderDeliveryDisplay,
} from "utils/orderDeliveryDisplay"
import { brandShowsScheduledDeliveryDateInOrders } from "utils/deliveryProfileHelpers"
import {
  getSystemStatusDisplay,
  getPosStatusLabel,
  getPosStatusColor,
  getSystemOrderStatusLabel,
} from "utils/orderStatusUtils"
import { getCurrencyLabel } from "utils/currency"
import useAllowExports from "hooks/useAllowExports"
import {
  buildSingleOrderExportPayload,
  canExportOrders,
  downloadOrderExport,
} from "utils/orderExportUtils"
import ConfirmModal from "components/Common/ConfirmModal"
import { isTenantOwnerAdmin } from "utils/isTenantOwnerAdmin"
import { hasWalletRelatedPermission } from "utils/walletPermissionUtils"

const toDateTimeLocalValue = value => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const pad = num => String(num).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const isDeliveryOrderDelivered = order => {
  if (!order) return true
  const orderStatus = Number(order.orderStatus)
  if (orderStatus === 4) return true
  if (String(order.deliveryStatus || "").toLowerCase() === "delivered") {
    return true
  }
  if (order.status === "Delivered") return true
  if (order.deliveredAt) return true
  return false
}

const isDeliveryOrderCancelled = order => {
  if (!order) return false
  if (Number(order.orderStatus) === 5) return true
  if (String(order.deliveryStatus || "").toLowerCase() === "cancelled") {
    return true
  }
  if (order.status === "Cancelled") return true
  return false
}

/** Rider statuses from pickup onward — estimated delivery date can no longer be edited. */
const RIDER_STATUSES_LOCKING_DELIVERY_DATE = [
  "PickedUp",
  "OutForDelivery",
  "ArrivedDrop",
  "Delivered",
]

const isRiderPastPickup = order => {
  if (!order) return false
  if (RIDER_STATUSES_LOCKING_DELIVERY_DATE.includes(order.status)) {
    return true
  }
  return false
}

const ViewOrder = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Order Details ")

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )

  const allowExports = useAllowExports()

  const hasWalletPermission = hasWalletRelatedPermission(
    userRole,
    accountType,
    accountScope
  )

  useEffect(() => {
    const isPermissionView = isPermissionEnabled(
      userRole,
      9,
      "order_1",
      accountType
    )
    if (!isPermissionView) {
      navigate("/dashboard")
    }
  }, [])

  const { loading, isLoading, orderDetails } = useSelector(
    state => state.Orders
  )

  const orderUpdateSubPermissions = getSubPermissions(
    userRole,
    9,
    "order_2",
    accountType,
    accountScope
  )

  const canUpdateDeliveryDate =
    isTenantOwnerAdmin() ||
    isPermissionEnabled(
      userRole,
      9,
      "update_delivery_date",
      accountType,
      accountScope
    ) ||
    Boolean(orderUpdateSubPermissions?.update_delivery_date?.isEnabled)

  const isDeliveryEditable =
    orderDetails?.orderType === "delivery" &&
    !isDeliveryOrderDelivered(orderDetails) &&
    !isDeliveryOrderCancelled(orderDetails) &&
    !isRiderPastPickup(orderDetails) &&
    canUpdateDeliveryDate

  const minDeliveryDateTime = useMemo(
    () => toDateTimeLocalValue(orderDetails?.createdAt),
    [orderDetails?.createdAt]
  )

  const [isEditingDeliveryDate, setIsEditingDeliveryDate] = useState(false)
  const [pendingDeliveryDate, setPendingDeliveryDate] = useState("")
  const [showDeliveryDateConfirm, setShowDeliveryDateConfirm] = useState(false)

  useEffect(() => {
    setPendingDeliveryDate(
      toDateTimeLocalValue(orderDetails?.estimatedDeliveryDate)
    )
    setIsEditingDeliveryDate(false)
  }, [orderDetails?.estimatedDeliveryDate, orderDetails?._id])

  const { brands: assignedBrandsFromHook } = useAssignedBrands()
  const { byCode: tenantPosByCode } = useTenantPosConnections()
  const { assignedBrands } = useSelector(state => state.Brands)
  const brandsForPos = assignedBrands?.length
    ? assignedBrands
    : assignedBrandsFromHook
  const posSource = useMemo(
    () => buildOrderPosSource({ order: orderDetails, assignedBrands: brandsForPos }),
    [orderDetails, brandsForPos]
  )
  const posIntegrationCode = resolveOrderPosIntegrationCode(posSource)
  const showPosDetails = shouldShowOrderPosDetails(posSource)
  const tenantConnection = useMemo(
    () =>
      getTenantIntegrationConnection(
        posIntegrationCode,
        tenantPosByCode,
        orderDetails?.posUi
      ),
    [posIntegrationCode, tenantPosByCode, orderDetails?.posUi]
  )
  const branchLinked = isOrderPosBranchLinked(orderDetails, posIntegrationCode)
  const hasPos = hasExternalPosIntegration(posSource)
  const isSalasa = isSalasaProvider(posSource)
  const showPosStatusColumn = isFnbPosProvider(posSource)
  const assignedBrandEntry = useMemo(
    () => findAssignedBrandForOrder(orderDetails, assignedBrands),
    [orderDetails, assignedBrands]
  )
  const deliveryDisplay = useMemo(
    () => resolveOrderDeliveryDisplay(orderDetails, assignedBrandEntry),
    [orderDetails, assignedBrandEntry]
  )
  const showScheduledDeliveryDate = useMemo(
    () => brandShowsScheduledDeliveryDateInOrders(assignedBrandEntry),
    [assignedBrandEntry]
  )
  const deliveryDateValue = useMemo(
    () => resolveOrderDeliveryDate(orderDetails),
    [orderDetails]
  )
  const canEditDeliveryDate =
    showScheduledDeliveryDate &&
    isDeliveryEditable &&
    deliveryDisplay.showEstimatedDeliveryDate
  const canEditOrder =
    isTenantOwnerAdmin() ||
    isPermissionEnabled(userRole, 9, "order_2", accountType, accountScope) ||
    Boolean(orderUpdateSubPermissions?.update_orders?.isEnabled)
  const isB2cListing = location.pathname.includes("/b2c-orders")

  const canExportOrder =
    Boolean(id) &&
    Boolean(orderDetails?._id) &&
    allowExports &&
    canExportOrders({
      userRole,
      accountType,
      isB2c: isSalasa,
    })

  const runSingleOrderExport = async exportType => {
    if (!id || !orderDetails?._id) return

    const payload = buildSingleOrderExportPayload({
      orderId: id,
      exportType,
      serviceProvider: orderDetails?.serviceProvider || orderDetails?.brandIntegration,
      includeServiceProvider: hasPos || isB2cListing,
      includePosColumns: showPosStatusColumn || isB2cListing,
      includeSalasaColumns: isB2cListing,
      brandId: orderDetails?.brandId?._id || orderDetails?.brandId,
    })

    const invoiceLabel = orderDetails?.invoiceNo || id
    await downloadOrderExport({
      dispatch,
      t,
      payload,
      fileName: t("Order {{invoiceNo}}", { invoiceNo: invoiceLabel }),
    })
  }

  const handleExcelDownload = async () => {
    await runSingleOrderExport("xlsx")
  }

  const exportToCSV = async () => {
    await runSingleOrderExport("csv")
  }

  const fetchOrderDetails = () => {
    dispatch(getOrderDetails(id))
  }

  const handleDeliveryDateSaveClick = () => {
    if (!pendingDeliveryDate) {
      dispatch(
        showAlert({
          text: t("Please select a delivery date and time"),
          type: "error",
        })
      )
      return
    }

    const selectedDate = new Date(pendingDeliveryDate)
    const minDate = new Date(orderDetails?.createdAt)
    if (selectedDate.getTime() < minDate.getTime()) {
      dispatch(
        showAlert({
          text: t("Delivery date cannot be before the order creation date"),
          type: "error",
        })
      )
      return
    }

    setShowDeliveryDateConfirm(true)
  }

  const handleConfirmDeliveryDateUpdate = () => {
    dispatch(
      updateOrderDeliveryDate({
        id,
        estimatedDeliveryDate: new Date(pendingDeliveryDate).toISOString(),
      })
    )
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeliveryDateConfirm(false)
          setIsEditingDeliveryDate(false)
          fetchOrderDetails()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    fetchOrderDetails()
  }, [])

  function formatDate(dateString) {
    const date = new Date(dateString)
    const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")} ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date
      .getSeconds()
      .toString()
      .padStart(2, "0")}`
    return formattedDate
  }

  const copyToClipboard = data => {
    const dataString = JSON.stringify(data, null, 2)

    const textarea = document.createElement("textarea")
    textarea.value = dataString
    textarea.setAttribute("readonly", "")
    textarea.style.position = "absolute"
    textarea.style.left = "-9999px"
    document.body.appendChild(textarea)

    try {
      textarea.select()
      const successful = document.execCommand("copy")
      if (successful) {
        dispatch(
          showAlert({ text: t("Data copied to clipboard!"), type: "success" })
        )
      } else {
        dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
      }
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
    } finally {
      document.body.removeChild(textarea)
    }
  }

  const handleSentOnPOS = () => {
    const payload = {
      orderId: orderDetails?._id,
    }
    dispatch(syncOrderWithPos(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          fetchOrderDetails()
        } else {
          dispatch(
            showAlert({
              text: t(POS_PUSH_FAILED_MESSAGE),
              type: "error",
            })
          )
        }
      })
      .catch(() => {
        dispatch(
          showAlert({
            text: t(POS_PUSH_FAILED_MESSAGE),
            type: "error",
          })
        )
      })
  }

  const toReadableFormat = str => {
    if (!str) return ""

    return str
      .toLowerCase() // ensure consistent casing
      .replace(/_/g, " ") // replace underscores with spaces
      .replace(/^\w/, c => c.toUpperCase()) // capitalize the first letter
  }

  const riderProgressLabel = status => {
    if (!status) return "-"
    const labels = {
      Assigned: t("Assigned"),
      Accepted: t("Accepted"),
      ArrivedPickup: t("Arrived at Branch"),
      PickedUp: t("Picked Up"),
      OutForDelivery: t("Out for Delivery"),
      ArrivedDrop: t("Arrived at Customer"),
      Delivered: t("Delivered"),
      Failed: t("Failed"),
      Cancelled: t("Cancelled"),
    }
    return labels[status] || toReadableFormat(status) || status
  }

  const riderAvailabilityLabel = status => {
    const labels = {
      Online: t("Online"),
      Offline: t("Offline"),
      OnDelivery: t("On Delivery"),
      TemporarilyUnavailable: t("Temporarily Unavailable"),
    }
    return labels[status] || status || "-"
  }

  const getAssignedRider = () => {
    const partner = orderDetails?.deliveryPartnerId
    if (partner && typeof partner === "object") return partner
    return null
  }

  const getRiderProgressStatus = () => {
    const riderStatuses = [
      "Assigned",
      "Accepted",
      "ArrivedPickup",
      "PickedUp",
      "OutForDelivery",
      "ArrivedDrop",
      "Delivered",
      "Failed",
      "Cancelled",
    ]
    if (
      orderDetails?.status &&
      riderStatuses.includes(orderDetails.status)
    ) {
      return orderDetails.status
    }
    const deliveryStatusFallbacks = {
      assigned: "Assigned",
      in_progress: "Accepted",
      delivered: "Delivered",
      cancelled: "Cancelled",
    }
    return deliveryStatusFallbacks[orderDetails?.deliveryStatus] || null
  }

  const assignedRider = getAssignedRider()

  const getOrderStatusText = () => {
    const display = getSystemStatusDisplay(orderDetails, t)
    if (display?.text && display.text !== "-") {
      return display.text
    }

    if (
      orderDetails?.status !== null &&
      orderDetails?.status !== undefined &&
      orderDetails?.status !== ""
    ) {
      return riderProgressLabel(orderDetails.status)
    }

    return "-"
  }

  const systemStatusDisplay = getSystemStatusDisplay(orderDetails, t)
  const thirdPartyStatusLabel = getPosStatusLabel(orderDetails, t)
  const thirdPartyStatusColor = getPosStatusColor(orderDetails)

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          {loading ? (
            <ShimmerSimpleGallery card imageHeight={500} col={3} row={1} />
          ) : (
            <Row>
              <Col xl="4">
                <Card className="card_100_24">
                  <CardBody>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <CardTitle className="mb-0 d-flex align-items-center">
                        <BackButton />
                        {t("Order Details")}
                      </CardTitle>
                      {canExportOrder && (
                        <UncontrolledDropdown className="dropdown d-inline-block">
                          <DropdownToggle
                            type="menu"
                            className="btn btn-success"
                            id="orderExportDropdown"
                          >
                            <i className="mdi mdi-dots-vertical"></i>
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem tag="button" onClick={exportToCSV}>
                              {t("Download CSV")}
                            </DropdownItem>
                            <DropdownItem tag="button" onClick={handleExcelDownload}>
                              {t("Download XLSX")}
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      )}
                    </div>
                    <div className="table-responsive capitalize_td">
                      {/* {userDetails && Object.values(userDetails).length === 0 ? (
                      <p className="text-center">No order found</p>
                    ) : ( */}
                      <Table className="mb-0">
                        <tbody>
                          <tr>
                            <th scope="row">{t("Order No")}</th>
                            <td>{orderDetails.invoiceNo || "-"}</td>
                          </tr>
                          {isSalasa && (
                            <tr>
                              <th scope="row">{t("Salasa Order Id")}</th>
                              <td>{orderDetails.salasaOrderId || "-"}</td>
                            </tr>
                          )}
                          <tr>
                            <th scope="row">{t("Order Type")}</th>
                            <td className="text-capitalize">
                              {orderDetails?.orderType
                                ? t(orderDetails.orderType)
                                : "-"}
                            </td>
                          </tr>
                          {/* <tr>
                          <th scope="row">Coupon</th>
                          <td>{orderDetails.coupon || "-"}</td>
                        </tr> */}

                          <tr>
                            <th scope="row">{t("Country")}</th>
                            <td>
                              {language === "sa"
                                ? orderDetails?.countryId?.nameAr
                                : orderDetails?.countryId?.name || "-"}
                            </td>
                          </tr>
                          {/* <tr>
                            <th scope="row">Invoice Url</th>
                            <td>{orderDetails.invoiceUrl || "-"}</td>
                          </tr> */}
                          <tr>
                            <th scope="row">{t("Branch Name")}</th>
                            <td>
                              {language === "sa"
                                ? orderDetails?.branchId?.nameAr
                                : orderDetails?.branchId?.name || "-"}
                            </td>
                          </tr>
                          {/* <tr>
                            <th scope="row">Payment Method</th>
                            <td>{orderDetails.paymentMethod || "-"}</td>
                          </tr> */}

                          {/* <tr>
                            <th scope="row">Schedule Type</th>
                            <td>{orderDetails.scheduleType || "-"}</td>
                          </tr> */}
                          {deliveryDisplay.showPromiseDateTime && (
                            <tr>
                              <th scope="row">{t("Promise Date")}</th>
                              <td>
                                {formatDateTime(
                                  orderDetails.promiseDateTime
                                )?.split(" ")[0] || "-"}
                              </td>
                            </tr>
                          )}
                          {deliveryDisplay.showPromiseDateTime && (
                            <tr>
                              <th scope="row">{t("Promise Time")}</th>
                              <td>
                                {formatDateTime(
                                  orderDetails.promiseDateTime
                                )?.split(" ")[1] || "-"}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <th scope="row">{t("Order Status")}</th>
                            <td style={{ color: systemStatusDisplay.color }}>
                              {getOrderStatusText()}
                            </td>
                          </tr>
                          {hasPos && (
                            <tr>
                              <th scope="row">{t("POS Status")}</th>
                              <td style={{ color: thirdPartyStatusColor }}>
                                {thirdPartyStatusLabel}
                              </td>
                            </tr>
                          )}
                          {orderDetails.orderType === "delivery" &&
                            showScheduledDeliveryDate && (
                            <tr>
                              <th scope="row">{t("Delivery Date")}</th>
                              <td>
                                {canEditDeliveryDate && isEditingDeliveryDate ? (
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <Input
                                      type="datetime-local"
                                      value={pendingDeliveryDate}
                                      min={minDeliveryDateTime}
                                      onChange={e =>
                                        setPendingDeliveryDate(e.target.value)
                                      }
                                      style={{ maxWidth: 240 }}
                                    />
                                    <Button
                                      color="success"
                                      size="sm"
                                      disabled={isLoading}
                                      onClick={handleDeliveryDateSaveClick}
                                    >
                                      {t("Save")}
                                    </Button>
                                    <Button
                                      color="secondary"
                                      size="sm"
                                      outline
                                      onClick={() => {
                                        setIsEditingDeliveryDate(false)
                                        setPendingDeliveryDate(
                                          toDateTimeLocalValue(
                                            orderDetails.estimatedDeliveryDate
                                          )
                                        )
                                      }}
                                    >
                                      {t("Cancel")}
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <span>
                                      {deliveryDateValue
                                        ? formatDateTime(deliveryDateValue)
                                        : "-"}
                                    </span>
                                    {canEditDeliveryDate && (
                                      <Button
                                        color="link"
                                        className="p-0"
                                        onClick={() =>
                                          setIsEditingDeliveryDate(true)
                                        }
                                      >
                                        <i className="bx bx-edit-alt fs-5" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          {orderDetails.orderType === "delivery" &&
                            deliveryDisplay.showAsapExpectedTime && (
                            <tr>
                              <th scope="row">{t("Delivery Type")}</th>
                              <td>{t("ASAP")}</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                      {/* )} */}

                      {showPosDetails && (
                        <OrderPosDetailsPanel
                          order={orderDetails}
                          posSource={posSource}
                          posIntegrationCode={posIntegrationCode}
                          tenantConnection={tenantConnection}
                          branchLinked={branchLinked}
                          onManualPush={handleSentOnPOS}
                          pushLoading={isLoading}
                          canEdit={canEditOrder}
                          copyToClipboard={copyToClipboard}
                        />
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl="4">
                <Card className="card_100_24">
                  <CardBody>
                    <CardTitle className="mb-4">
                      {t("Payments Details")}
                    </CardTitle>
                    <div className="table-responsive">
                      <Table className="table-nowrap mb-0">
                        <tbody>
                          <tr>
                            <th scope="row">{t("Coupon")}</th>
                            <td>{orderDetails?.coupon?.code || "-"} </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Discount Type")}</th>
                            <td>
                              {orderDetails.discountType
                                ? t(orderDetails.discountType)
                                : "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Total")}</th>
                            <td>
                              {orderDetails?.splitPayments?.total}{" "}
                              {getCurrencyLabel(
                                orderDetails?.splitPayments?.currency
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("VAT Amount")}</th>
                            <td>
                              {orderDetails?.splitPayments?.vatAmount}{" "}
                              {getCurrencyLabel(
                                orderDetails?.splitPayments?.currency
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("VAT %")}</th>
                            <td>{orderDetails?.splitPayments?.vatValue}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Online")}</th>
                            <td>
                              {orderDetails?.splitPayments?.gatewayAmount}{" "}
                              {getCurrencyLabel(
                                orderDetails?.splitPayments?.currency
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Discounted Amount")}</th>
                            <td>
                              {orderDetails?.splitPayments?.discountedAmount}{" "}
                              {getCurrencyLabel(
                                orderDetails?.splitPayments?.currency
                              )}
                            </td>
                          </tr>
                          {hasWalletPermission && (
                            <tr>
                              <th scope="row">{t("Wallet Amount")}</th>
                              <td>
                                {orderDetails?.splitPayments?.walletAmount}{" "}
                                {getCurrencyLabel(
                                  orderDetails?.splitPayments?.currency
                                )}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <th scope="row">{t("Loyalty Amount")}</th>
                            <td>
                              {orderDetails?.splitPayments?.rewardAmount}{" "}
                              {getCurrencyLabel(
                                orderDetails?.splitPayments?.currency
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Loyalty Redeemed points")}</th>
                            <td>{orderDetails?.splitPayments?.rewardPoints}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Loyalty Earned Points")}</th>
                            <td>{orderDetails?.earnPoints}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Payment Method")}</th>
                            <td>{t(orderDetails?.paymentMethod) || "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Payment Status")}</th>
                            <td>
                              {orderDetails.paymentStatus === 0 && t("Pending")}
                              {orderDetails.paymentStatus === 1 && t("Paid")}
                              {orderDetails.paymentStatus === 2 &&
                                t("Refunded")}
                            </td>
                          </tr>
                          {orderDetails.paymentEvidenceUrl && (
                            <tr>
                              <th scope="row">{t("Payment Evidence")}</th>
                              <td>
                                <a
                                  href={orderDetails.paymentEvidenceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t("View Evidence")}
                                </a>
                              </td>
                            </tr>
                          )}
                          {orderDetails.paymentStatus === 2 && (
                            <tr>
                              <th scope="row">{t("Refund At")}</th>
                              <td>
                                {formatDate(
                                  orderDetails?.transaction?.refundInfo
                                    ?.refundAt
                                )}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <th scope="row">{t("Payment Reference")}</th>
                            <td>
                              {orderDetails?.transaction?.transactionId || "-"}{" "}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      {/* )} */}
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl="4">
                <Card
                  className={
                    orderDetails?.orderType === "delivery"
                      ? "mb-3"
                      : "card_100_24"
                  }
                >
                  <CardBody>
                    <CardTitle className="mb-4 d-flex align-items-center">
                      {t("Customer Details")}
                    </CardTitle>
                    <div className="table-responsive">
                      {orderDetails &&
                      Object.values(orderDetails).length === 0 ? (
                        <p className="text-center">
                          {t("No customer details found")}
                        </p>
                      ) : (
                        <Table className="table-nowrap mb-0">
                          <tbody>
                            <tr>
                              <th scope="row">{t("Name")}</th>
                              <td className="text-capitalize">
                                <Link
                                  to={`/customers/customer-details?id=${orderDetails?.userId?._id}`}
                                  className="text-primary text-underline"
                                >
                                  {`${orderDetails?.userId?.firstName} ${orderDetails?.userId?.lastName}` ||
                                    "-"}
                                </Link>
                              </td>
                            </tr>
                            {/* <tr>
                              <th scope="row">First Name</th>
                              <td className="text-capitalize">
                                {orderDetails?.userId?.firstName || "-"}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">Last Name</th>
                              <td className="text-capitalize">
                                {orderDetails?.userId?.lastName || "-"}
                              </td>
                            </tr> */}
                            <tr>
                              <th scope="row">{t("Email")}</th>
                              <td>{orderDetails?.userId?.email || "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Phone Code")}</th>
                              <td>
                                {orderDetails?.userId?.phoneCode ? (
                                  <LtrText>
                                    {orderDetails.userId.phoneCode}
                                  </LtrText>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Phone Number")}</th>
                              <td>
                                {orderDetails?.userId?.phoneNumber ? (
                                  <LtrText>
                                    {orderDetails.userId.phoneNumber}
                                  </LtrText>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Platform")}</th>
                              <td>{t(orderDetails?.platform) || "-"}</td>
                            </tr>
                            {/* <tr>
                              <th scope="row">Is Rated</th>
                              <td>
                                {orderDetails?.isUserRated
                                  ? "Yes"
                                  : "No" || "-"}
                              </td>
                            </tr> */}
                          </tbody>
                        </Table>
                      )}
                    </div>
                    {orderDetails?.orderType === "curbside" && (
                      <>
                        <CardTitle className=" mt-4 d-flex align-items-center">
                          {t("Vehicle Details")}
                        </CardTitle>
                        <div className="table-responsive">
                          <Table className="table-nowrap mb-0">
                            <tbody>
                              <tr>
                                <th scope="row">{t("Name")}</th>
                                <td>
                                  <div className="d-flex gap-1 align-items-center">
                                    {orderDetails?.userVehicleId?.vehicleId
                                      ?.logo && (
                                      <img
                                        src={
                                          orderDetails?.userVehicleId?.vehicleId
                                            ?.logo
                                        }
                                        width={20}
                                        height={20}
                                      />
                                    )}
                                    {(language === "sa"
                                      ? orderDetails?.userVehicleId?.vehicleId
                                          ?.brandAr ||
                                        orderDetails?.userVehicleId?.vehicleId
                                          ?.brand
                                      : orderDetails?.userVehicleId?.vehicleId
                                          ?.brand) || "-"}
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">{t("Color")}</th>
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <p
                                      className="mb-0"
                                      style={{
                                        background:
                                          orderDetails?.userVehicleId?.colorId
                                            ?.code,
                                        width: 18,
                                        height: 18,
                                        borderRadius: "50%",
                                      }}
                                    ></p>
                                    <p className="mt-0 mb-0">
                                      {(language === "sa"
                                        ? orderDetails?.userVehicleId?.colorId
                                            ?.nameAr ||
                                          orderDetails?.userVehicleId?.colorId
                                            ?.name
                                        : orderDetails?.userVehicleId?.colorId
                                            ?.name) || "-"}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">{t("Plate Number")}</th>
                                <td>
                                  {orderDetails?.userVehicleId?.plateNumber ||
                                    "-"}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">{t("Customer Status")}</th>
                                <td>
                                  {orderDetails?.arrivalAt
                                    ? t("Arrived")
                                    : "-"}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">{t("Parking Number")}</th>
                                <td>{orderDetails?.parkingNumber || "-"}</td>
                              </tr>
                              <tr>
                                <th scope="row">{t("Arrival Time")}</th>
                                <td>
                                  {orderDetails?.arrivalAt
                                    ? formatDateTime(orderDetails.arrivalAt)
                                    : "-"}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {orderDetails?.orderType === "delivery" && (
                  <Card className="mb-0">
                    <CardBody>
                      <CardTitle className="mb-4 d-flex align-items-center">
                        {t("Delivery Details")}
                      </CardTitle>
                      <div className="table-responsive capitalize_td">
                        <Table className="table-nowrap mb-0">
                          <tbody>
                            <tr>
                              <th scope="row">{t("Rider")}</th>
                              <td>
                                {assignedRider?._id || assignedRider?.id ? (
                                  <Link
                                    to={`/delivery-partners/${
                                      assignedRider._id || assignedRider.id
                                    }`}
                                    className="text-primary text-underline"
                                  >
                                    {assignedRider.name ||
                                      orderDetails.deliveryPartnerName}
                                  </Link>
                                ) : (
                                  assignedRider?.name ||
                                  orderDetails.deliveryPartnerName ||
                                  t("Not Assigned")
                                )}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Rider Status")}</th>
                              <td>
                                {riderProgressLabel(getRiderProgressStatus())}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Availability")}</th>
                              <td>
                                {assignedRider?.availabilityStatus
                                  ? riderAvailabilityLabel(
                                      assignedRider.availabilityStatus
                                    )
                                  : "-"}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Assigned At")}</th>
                              <td>
                                {orderDetails.assignedToRiderAt
                                  ? formatDateTime(
                                      orderDetails.assignedToRiderAt
                                    )
                                  : "-"}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Accepted At")}</th>
                              <td>
                                {orderDetails.acceptedAt
                                  ? formatDateTime(orderDetails.acceptedAt)
                                  : (orderDetails.status === "Accepted" ||
                                        orderDetails.deliveryStatus ===
                                          "in_progress") &&
                                      orderDetails.assignedToRiderAt
                                    ? formatDateTime(
                                        orderDetails.assignedToRiderAt
                                      )
                                    : "-"}
                              </td>
                            </tr>
                            {orderDetails.arrivedPickupAt && (
                              <tr>
                                <th scope="row">{t("Arrived at Branch")}</th>
                                <td>
                                  {formatDateTime(orderDetails.arrivedPickupAt)}
                                </td>
                              </tr>
                            )}
                            {orderDetails.pickedUpAt && (
                              <tr>
                                <th scope="row">{t("Picked Up")}</th>
                                <td>
                                  {formatDateTime(orderDetails.pickedUpAt)}
                                </td>
                              </tr>
                            )}
                            {orderDetails.arrivedDropAt && (
                              <tr>
                                <th scope="row">{t("Arrived at Customer")}</th>
                                <td>
                                  {formatDateTime(orderDetails.arrivedDropAt)}
                                </td>
                              </tr>
                            )}
                            {(orderDetails.deliveredAt ||
                              orderDetails.completedAt) && (
                              <tr>
                                <th scope="row">{t("Delivered")}</th>
                                <td>
                                  {formatDateTime(
                                    orderDetails.deliveredAt ||
                                      orderDetails.completedAt
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>
          )}

          {loading ? (
            <ShimmerSimpleGallery card imageHeight={500} col={1} row={1} />
          ) : (
            <Row>
              <Col>
                <Card>
                  <CardBody>
                    <CardTitle>{t("Order Items")}</CardTitle>
                    <div className="table-responsive">
                      {orderDetails?.orderItems?.length === 0 ? (
                        <p className="text-center my-3">
                          {t("No orders items found")}
                        </p>
                      ) : (
                        <Table className="table mb-0" hover>
                          <thead className="table-light">
                            <tr>
                              <th>{t("Item")}</th>
                              <th>{t("Name(English)")}</th>
                              <th>{t("Name(عربي)")}</th>
                              <th>{t("Price")}</th>
                              {/* <th>Modifier Price</th> */}
                              <th>{t("Quantity")}</th>
                              <th>{t("Total Price")}</th>
                              {/* <th>Modifier Groups</th>
                              <th>Modifiers</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {orderDetails?.orderItems?.map(item => (
                              <tr key={item?._id}>
                                <td>
                                  {item?.itemId?.imageUrl ? (
                                    <img
                                      style={{
                                        maxWidth: 50,
                                        maxHeight: 50,
                                        objectFit: "contain",
                                      }}
                                      src={item?.itemId?.imageUrl}
                                      alt=""
                                    />
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>
                                  <p className="fw-bold mb-2">
                                    {item?.itemId?.name || "-"}{" "}
                                  </p>
                                  {/* SHOW MODIFIERS */}
                                  {item?.modifiers?.map(subItem => (
                                    <React.Fragment key={subItem._id}>
                                      {subItem?.selectedModifiers?.map(
                                        (item2, index) => (
                                          <>
                                            <span key={index}>
                                              {item2?.modifier?.name}
                                            </span>
                                            <br />
                                          </>
                                        )
                                      )}
                                    </React.Fragment>
                                  ))}
                                </td>
                                <td>
                                  <p className="fw-bold mb-2">
                                    {item?.itemId?.nameAr || "-"}
                                  </p>
                                  {item?.modifiers?.map(subItem => (
                                    <React.Fragment key={subItem._id}>
                                      {subItem?.selectedModifiers?.map(
                                        (item2, index) => (
                                          <>
                                            <span key={index}>
                                              {item2?.modifier?.nameAr}
                                            </span>
                                            <br />
                                          </>
                                        )
                                      )}
                                    </React.Fragment>
                                  ))}
                                </td>
                                <td>
                                  <p className="fw-bold mb-2">
                                    {item.price}{" "}
                                    {getCurrencyLabel(
                                      orderDetails?.splitPayments?.currency
                                    )}
                                  </p>
                                  {item?.modifiers?.map(subItem => (
                                    <React.Fragment key={subItem._id}>
                                      {subItem?.selectedModifiers?.map(
                                        (item2, index) => (
                                          <>
                                            <span key={index}>
                                              {item2?.price}{" "}
                                              {getCurrencyLabel(
                                                orderDetails?.splitPayments
                                                  ?.currency
                                              )}
                                            </span>
                                            <br />
                                          </>
                                        )
                                      )}
                                    </React.Fragment>
                                  ))}
                                </td>
                                {/* <td>{item.mofifierPrice} SAR</td> */}
                                <td>
                                  <p className="fw-bold mb-2">
                                    {item.quantity}
                                  </p>
                                  {item?.modifiers?.map(subItem => (
                                    <React.Fragment key={subItem._id}>
                                      {subItem?.selectedModifiers?.map(
                                        (item2, index) => (
                                          <>
                                            <span key={index}>
                                              {item2?.quantity}
                                            </span>
                                            <br />
                                          </>
                                        )
                                      )}
                                    </React.Fragment>
                                  ))}
                                </td>
                                <td className="fw-bold">
                                  {item.totalPrice}{" "}
                                  {getCurrencyLabel(
                                    orderDetails?.splitPayments?.currency
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          {loading ? null : (
            <Row>
              <Col>
                <Card>
                  <CardBody>
                    <CardTitle>{t("Order History")}</CardTitle>
                    <div className="table-responsive">
                      {orderDetails?.statusHistory?.length === 0 ? (
                        <p className="text-center my-3">
                          {t("No orders history found")}
                        </p>
                      ) : isSalasa ? (
                        <Table className="table mb-0" hover>
                          <thead className="table-light">
                            <tr>
                              <th>{t("Status")}</th>
                              <th>{t("Date")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderDetails?.status_log?.length === 0 &&
                            orderDetails?.paymentStatus === 1 &&
                            orderDetails?.orderStatus === "Placed" ? (
                              <tr key={1}>
                                <td>Placed</td>
                                <td>
                                  {" "}
                                  {formatDateTime(orderDetails?.orderDate)}
                                </td>
                              </tr>
                            ) : (
                              orderDetails?.status_log?.map(item => (
                                <tr key={item._id}>
                                  <td>{toReadableFormat(item?.status)}</td>
                                  <td> {formatDateTime(item?.created_at)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      ) : (
                        <Table className="table mb-0" hover>
                          <thead className="table-light">
                            <tr>
                              <th>{t("Title")}</th>
                              <th>{t("Status")}</th>
                              <th>{t("Date")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderDetails?.statusHistory?.map(item => {
                              const titleEn = item?.title?.en
                              const titleAr = item?.title?.ar
                              const fallbackTitle = getSystemOrderStatusLabel(
                                item.status,
                                orderDetails.orderType,
                                t
                              )
                              // Prefer frontend i18n of the English title so Arabic locale
                              // stays consistent even when API ar/en strings vary.
                              const historyTitle =
                                language === "sa"
                                  ? (titleEn ? t(titleEn) : titleAr) ||
                                    fallbackTitle
                                  : titleEn || fallbackTitle

                              return (
                                <tr key={item._id}>
                                  <td>{historyTitle}</td>
                                  <td>
                                    {getSystemOrderStatusLabel(
                                      item.status,
                                      orderDetails.orderType,
                                      t
                                    )}
                                  </td>
                                  <td> {formatDateTime(item?.date)}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* {loading ? null : (
            <Row>
              <Col>
                <Card>
                  <CardBody className="overflow-auto">
                    <CardTitle>Order Ratings & Feedbacks</CardTitle>
                    <div
                      className="table-responsive"
                      style={{ maxHeight: 600 }}
                    >
                      {orderLoading ? (
                        <TableShimmer row={5} col={5} />
                      ) : orders.length === 0 ? (
                        <p className="text-center my-2">No orders found</p>
                      ) : (
                      <Table className="table mb-0">
                        <thead className="fixed_thead">
                          <tr>
                            <th>S. No.</th>
                            <th>Product Title</th>
                            <th>Rating</th>
                            <th>Feedback</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>1</td>
                            <td>Product</td>
                            <td>
                              <StarRatings
                                rating={4}
                                starRatedColor="#F1B44C"
                                starEmptyColor="#74788d"
                                numberOfStars={5}
                                name="rating"
                                starDimension="14px"
                                starSpacing="3px"
                              />
                            </td>
                            <td>Lorem ipsum dolor sit amet.</td>
                            <td>20-05-2024</td>
                          </tr>
                        </tbody>
                      </Table>
                       )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )} */}
        </Container>
      </div>
      <ConfirmModal
        show={showDeliveryDateConfirm}
        onCloseClick={() => setShowDeliveryDateConfirm(false)}
        onDeleteClick={handleConfirmDeliveryDateUpdate}
        buttonText={t("Confirm")}
        bodyTitle={t(
          "Are you sure you want to update the estimated delivery date for this order?"
        )}
        isLoading={isLoading}
      />
    </React.Fragment>
  )
}

export default ViewOrder
